import clsx from 'clsx';
import React, {useState} from 'react';
import {Stack, styled} from '@mui/material';
import MuiAvatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import {
  Body,
  Icon,
  Label,
  designSystemToken,
} from '@lightricks/react-design-system';
import formatMessageDate from '@/utils/formatMessageDate';
import translate from '@/utils/translate';
import type {VersionThread} from '@/types/conversationMessage';
import CampaignBadge from '@/components/campaign-badge';
import Link from '@/components/link';
import MessageCard from '@/components/message-card';
import styles from './AssetThreadCard.module.scss';

const ThreadBadge = styled(Badge)(() => ({
  '& .MuiBadge-badge': {
    backgroundColor: designSystemToken('semantic.fg.brand'),
    color: designSystemToken('semantic.fg.white'),
    fontSize: 10,
    minWidth: 16,
    height: 16,
    padding: '0 4px',
  },
}));

const ThumbnailAvatar = styled(MuiAvatar)({
  width: 32,
  height: 32,
  borderRadius: '4px',
});

const PlaceholderAvatar = styled(MuiAvatar)(() => ({
  width: 32,
  height: 32,
  borderRadius: '4px',
  backgroundColor: designSystemToken('semantic.bg.neutral-inverse'),
}));

type AssetThreadCardProps = {
  testID?: string;
  thread: VersionThread;
  assetUrl: string;
  onMarkUnread?: (messageId: string) => void;
};

const CONTENT_TYPE_LOCALE_LABEL_KEY: Record<string, string> = {
  photo: 'content_type.photo',
  video: 'content_type.video',
  blog: 'content_type.blog',
  mixed_media: 'content_type.mixed_media',
};

const SUBMISSION_STATUS_LOCALE_LABEL_KEY: Record<string, string> = {
  processing: 'submission_status.processing',
  pending: 'submission_status.pending',
  accepted: 'submission_status.accepted',
  rejected: 'submission_status.rejected',
  archived: 'submission_status.archived',
};

function AssetThreadCard({
  testID = 'asset-thread-card',
  thread,
  assetUrl,
  onMarkUnread,
}: AssetThreadCardProps) {
  const [isExpanded, setIsExpanded] = useState(thread.hasUnread);

  const contentTypeLabel = translate(
    CONTENT_TYPE_LOCALE_LABEL_KEY[thread.contentType] ?? 'content_type.other'
  );
  const statusLabel = translate(
    SUBMISSION_STATUS_LOCALE_LABEL_KEY[thread.status] ??
      'submission_status.other'
  );

  const handleToggleClick = (event: React.MouseEvent) => {
    // The toggle button is a sibling of the asset Link, not nested inside —
    // but we still stop propagation defensively so a misplaced parent listener
    // can't ever trigger a navigation when the user only meant to expand.
    event.stopPropagation();
    setIsExpanded((prev) => !prev);
  };

  return (
    <div className={styles.card} data-testid={testID}>
      <div className={styles.header}>
        <Link to={assetUrl} className={styles.headerLink}>
          <ThreadBadge
            badgeContent={thread.hasUnread ? thread.messageCount : 0}
            anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
          >
            {thread.thumbnailUrl ? (
              <ThumbnailAvatar
                src={thread.thumbnailUrl}
                variant="rounded"
                alt={translate('Asset thumbnail')}
              />
            ) : (
              <PlaceholderAvatar variant="rounded">
                <Icon
                  name="Navigation-Photos-Options"
                  appearance="neutral"
                  size="small"
                />
              </PlaceholderAvatar>
            )}
          </ThreadBadge>

          <Stack className={styles.headerInfo}>
            <Stack direction="row" className={styles.titleRow}>
              <Label size="md">{contentTypeLabel}</Label>
              <CampaignBadge campaignName={thread.campaignName} />
            </Stack>
            <Stack direction="row" className={styles.metaRow}>
              <Body
                size="sm"
                color={designSystemToken('semantic.fg.secondary')}
                className={styles.metaItem}
              >
                {translate('version-short', {n: thread.versionNumber})}
              </Body>
              <Body
                size="sm"
                color={designSystemToken('semantic.fg.secondary')}
                className={clsx(styles.metaItem, styles.metaItemTruncate)}
              >
                {statusLabel}
              </Body>
              <Body
                size="sm"
                color={designSystemToken('semantic.fg.secondary')}
                className={styles.metaItem}
              >
                {translate('thread-message-count', {
                  count: thread.messageCount,
                })}
              </Body>
            </Stack>
          </Stack>
        </Link>

        <button
          type="button"
          className={styles.toggleButton}
          onClick={handleToggleClick}
          aria-expanded={isExpanded}
          aria-label={translate(isExpanded ? 'Collapse' : 'Expand')}
        >
          <Icon
            name={
              isExpanded
                ? 'Actions-Arrow-Down-Small'
                : 'Actions-Arrow-Forward-Small'
            }
            appearance="neutral"
            size="small"
          />
        </button>
      </div>

      {isExpanded ? (
        <Stack className={styles.messagesContainer}>
          {thread.messages.map((message) => {
            const displayName =
              message.sender?.name || message.signerDisplayName || '';
            const isBrand =
              message.sender?.kind === 'brand' || !!message.isMerchantFeedback;
            return (
              <MessageCard
                key={message.id}
                messageId={message.id}
                displayName={displayName}
                avatarUrl={message.sender?.imageUrl}
                formattedDate={formatMessageDate(message.createdAt)}
                body={message.body}
                bodyHtml={message.bodyHtml}
                negotiationSnapshot={message.negotiationSnapshot}
                attachments={message.messageAttachments}
                isBrand={isBrand}
                isBrandFeedbackToCreator={!!message.isMerchantFeedback}
                showCampaignBadge={false}
                onMarkUnread={onMarkUnread}
              />
            );
          })}
        </Stack>
      ) : null}
    </div>
  );
}

export default AssetThreadCard;
